import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getLuxPlusSubscriptionRenewalDate } from 'selectors/luxPlusSelectors'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusManageAccountModalMembershipCancellation from './LuxPlusManageAccountModalMembershipCancellation'
import LuxPlusManageAccountModalOverview from './LuxPlusManageAccountModalOverview'
import LuxPlusManageAccountModalRenewalCancellation from './LuxPlusManageAccountModalRenewalCancellation'
import LuxPlusManageAccountModalRenewalCancellationConfirmation from './LuxPlusManageAccountModalRenewalCancellationConfirmation'
import LuxPlusManageAccountModalRenewalCancellationSuccess from './LuxPlusManageAccountModalRenewalCancellationSuccess'
import LuxPlusManageAccountModalRenewalReactivation from './LuxPlusManageAccountModalRenewalReactivation'
import LuxPlusManageAccountModalRenewalReactivationSuccess from './LuxPlusManageAccountModalRenewalReactivationSuccess'
import LuxPlusManageAccountModalUpdatePaymentMethod from './LuxPlusManageAccountModalUpdatePaymentMethod'
import LuxPlusManageAccountModalUpdatePaymentMethodFailure from './LuxPlusManageAccountModalUpdatePaymentMethodFailure'
import LuxPlusManageAccountModalUpdatePaymentMethodSuccess from './LuxPlusManageAccountModalUpdatePaymentMethodSuccess'
import LuxPlusManageAccountModalAddBackupPaymentMethod from './LuxPlusManageAccountModalAddBackupPaymentMethod'

type ManageMode =
  | 'overview'
  | 'membership-cancellation'
  | 'renewal-cancellation'
  | 'renewal-cancellation-confirmation'
  | 'renewal-cancellation-success'
  | 'renewal-reactivation'
  | 'renewal-reactivation-success'
  | 'payment-method-update'
  | 'payment-method-update-success'
  | 'payment-method-update-failure'
  | 'backup-payment-method-add'

interface Props {
  initialMode?: ManageMode
  onPaymentMethodUpdateSuccess?: () => void
}

function LuxPlusManageAccountModal({ initialMode = 'overview', onPaymentMethodUpdateSuccess }: Props) {
  const intl = useIntl()
  const [mode, setMode] = useState<ManageMode>(initialMode)
  const [paymentFailureBackMode, setPaymentFailureBackMode] = useState<ManageMode>('payment-method-update')

  const { item } = useAppSelector(state => state.auth.account.luxPlus.member.subscription)
  const renewalDate = useAppSelector(getLuxPlusSubscriptionRenewalDate)

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)

  return <ModalBase size="S" height="max" tabletHeight="auto">
    {!item && <ModalContent>
      <ModalBody>
        <LoadingIndicator inline>{intl.formatMessage({
          id: 'p8Cm7g3Ju1H67rSjBhKW',
          defaultMessage: 'Fetching account information',
          description: 'Loading indicator text while fetching account subscription data',
        })}
        </LoadingIndicator>
      </ModalBody>
    </ModalContent>}
    {!!item && <>
      {mode === 'overview' && <LuxPlusManageAccountModalOverview
        item={item}
        onCancelMembership={() => setMode('membership-cancellation')}
        onCancelRenew={() => setMode('renewal-cancellation-confirmation')}
        onReactivateRenew={() => setMode('renewal-reactivation')}
        onUpdatePaymentMethod={() => setMode('payment-method-update')}
        onAddBackupPaymentMethod={() => setMode('backup-payment-method-add')}
      />}
      {mode === 'membership-cancellation' && <LuxPlusManageAccountModalMembershipCancellation
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-confirmation' && <LuxPlusManageAccountModalRenewalCancellationConfirmation
        item={item}
        onCancelRenewal={() => setMode('renewal-cancellation')}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation' && <LuxPlusManageAccountModalRenewalCancellation
        item={item}
        onRenewCancelSuccess={() => setMode('renewal-cancellation-success')}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-cancellation-success' && <LuxPlusManageAccountModalRenewalCancellationSuccess
        item={item}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-reactivation' && <LuxPlusManageAccountModalRenewalReactivation
        item={item}
        onReactivationSuccess={() => isLoyaltyEnabled ? setMode('renewal-reactivation-success') : setMode('overview')}
        onBack={() => setMode('overview')}
      />}
      {mode === 'renewal-reactivation-success' && <LuxPlusManageAccountModalRenewalReactivationSuccess
        item={item}
        onBack={() => setMode('overview')}
      />}
      {mode === 'payment-method-update' && <LuxPlusManageAccountModalUpdatePaymentMethod
        item={item}
        renewalDate={renewalDate}
        onUpdatePaymentMethodSuccess={() => {
          onPaymentMethodUpdateSuccess?.()
          setMode('payment-method-update-success')
        }}
        onUpdatePaymentMethodFailure={() => {
          setPaymentFailureBackMode('payment-method-update')
          setMode('payment-method-update-failure')
        }}
        onBack={() => setMode('overview')}
      />}
      {mode === 'payment-method-update-success' && <LuxPlusManageAccountModalUpdatePaymentMethodSuccess
        onBack={() => setMode('overview')}
      />}
      {mode === 'payment-method-update-failure' && <LuxPlusManageAccountModalUpdatePaymentMethodFailure
        onBack={() => setMode(paymentFailureBackMode)}
      />}
      {mode === 'backup-payment-method-add' && <LuxPlusManageAccountModalAddBackupPaymentMethod
        item={item}
        renewalDate={renewalDate}
        onSuccess={() => setMode('overview')}
        onFailure={() => {
          setPaymentFailureBackMode('backup-payment-method-add')
          setMode('payment-method-update-failure')
        }}
        onBack={() => setMode('overview')}
      />}
    </>}
  </ModalBase>
}

export default LuxPlusManageAccountModal
